import { View } from 'react-native';
import React, { useEffect } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { useElementPosition } from '../../hooks/useElementPosition';

export type Dimensions = {
    width: number;
    height: number;
};

export type SizedViewOwnProps = {
    style?: StyleProp<ViewStyle>;
    children: (dimensions: Dimensions) => React.ReactNode;
    testID?: string;
    placeholderStyle?: StyleProp<ViewStyle>;
    lazyEvaluateWidth?: boolean;
    /**
     * Minimum offset to consider a dimension change significant enough to trigger a re-render.
     */
    minDimensionOffset?: number;
};

export type SizedViewProps = SizedViewOwnProps;

const SizedView: React.FC<SizedViewProps> = (props) => {
    const { ref, position, onLayout } = useElementPosition({});
    const [childDimensions, setChildDimensions] = React.useState<Dimensions | null>(null);
    const lastDimensionRef = React.useRef<Dimensions>({ width: 0, height: 0 });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (position) {
            const newDimensions = {
                width: position.width,
                height: position.height,
            };

            const shouldUpdate = (a: Dimensions, b: Dimensions) => {
                return (
                    Math.abs(a.width - b.width) > (props.minDimensionOffset || 1) ||
                    Math.abs(a.height - b.height) > (props.minDimensionOffset || 1)
                );
            };

            if (shouldUpdate(lastDimensionRef.current, newDimensions)) {
                setChildDimensions(newDimensions);
            }

            lastDimensionRef.current = newDimensions;
        }
    });

    const placeholder = props.placeholderStyle ? <View style={props.placeholderStyle} /> : null;

    return (
        <View ref={ref} style={props.style} testID={props.testID} onLayout={onLayout}>
            {position && childDimensions && (props.lazyEvaluateWidth || !!childDimensions.width)
                ? props.children(childDimensions)
                : placeholder}
        </View>
    );
};

export default React.memo(SizedView);
